import { TestImage } from "@assets/constant/index.constant";
import { Button } from "@components/ui/button";
import { GeneralCard, TableCard } from "@ui/internal/components/card";
import { useParams, useSearchParams } from "react-router";
import { TemplateForm } from "../form/certificate.form";
import { METHODS, QueryData } from "@utils/interface/index.interface";
import { CertificateData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { useMetaStore, useSelectionStore } from "@utils/states/index.state";
import { usePathname } from "@utils/hook/index.hook";
import { format } from "date-fns";
import { endpoint } from "@services/api.services";
import { useEffect, useRef, useState } from "react";
import { TemplateColumnList } from "../column/index.column";
import {
  exportToExcel,
  formatNumber,
  showToast,
} from "@utils/helper/index.helper";
import FilterPopover from "@ui/internal/components/filter-popover";
import FileUpload from "@ui/internal/components/file-upload/FileUpload";
import UploadToCloud from "@ui/internal/components/filetocloud";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ActiviteGiveAway,
  GetActiveGiveAway,
  ImageUploadGiveAway,
  ToggleShowGiveawayTemplate,
  uploadToGallery,
} from "@services/giveAwayServices";
import { Upload, UploadCloudIcon } from "lucide-react";
import { Tooltip } from "@radix-ui/react-tooltip";
import { Switch } from "@components/ui/switch";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const index = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { search, date } = useMetaStore();
  const pathname = usePathname();
  const type = pathname;
  const endpointData = endpoint["giveaway-templates"];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [activateGiveaway, setActivateGiveaway] = useState(false);

  const {
    data: ActiveStatus,
    isPending: activePending,
    isError: activeError,
    refetch: refetchActive,
  } = useQuery({
    queryKey: ["activateGiveAway"],
    queryFn: GetActiveGiveAway,
    enabled: true,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (ActiveStatus && ActiveStatus.data) {
      setActivateGiveaway(ActiveStatus.data.is_giveaway);
    }
  }, [ActiveStatus]);

  /* ================================================= api calls logic ================================================= */
  const { data, isPending, refetch, error } = useListData<
    QueryData<CertificateData>
  >({
    params: {
      page: searchParams.get("tpage") || 1,
      size: searchParams.get("per") || 10,
      search,
      created_date__gte: date.from ? format(date.from, "yyyy-MM-dd") : "",
      created_date__lte: date.to ? format(date.to, "yyyy-MM-dd") : "",
      ordering: searchParams.get("ordering") || "order",
      course_batch__name:
        searchParams
          .get("data-category")
          ?.toLocaleLowerCase()
          ?.split("-")[0] === "all"
          ? ""
          : searchParams
              .get("data-category")
              ?.toLocaleLowerCase()
              ?.split("-")[0] || "",
    },
    endpoint: endpointData,
    key: `${pathname}-listing`,
    window: true,
  });

  /* ================================================= Mutation logic ================================================= */
  const { mutate: ActivateGiveAway, isPending: ActivatePending } = useMutation({
    mutationFn: ActiviteGiveAway,
    onSuccess: (response: any) => {
      refetchActive();
      // Extract the image URL/ID from the response - adjust based on your API response structure
      showToast({
        title: "Activate Giveaway",
        description: response.message || "Activate Giveaway Successfull",
        type: "success",
      });
    },
    onError: (error) => {
      console.error("Ishow update failed", error);
      showToast({
        title: "Activate Giveaway",
        description: error.message || "Activate Giveaway Failed",
        type: "error",
      });
      // setIsUploading(false);
      // Handle error - show toast notification, etc.
    },
  });

  // First mutation: Upload to gallery
  const uploadToGalleryMutation = useMutation({
    mutationFn: uploadToGallery,
    onSuccess: (response) => {
      refetch();
      // Extract the image URL/ID from the response - adjust based on your API response structure
      const uploadedImageUrl = response.data?.image || response.data?.url;

      // Trigger the second mutation
      if (uploadedImageUrl) {
        imageUploadGiveAwayMutation.mutate({
          IsGiveaway: ActiveStatus?.data?.is_giveaway, // or whatever value you need
          GiveawayImg: uploadedImageUrl,
        });
      }
    },
    onError: (error) => {
      toast.error("Gallery upload failed:");
      setIsUploading(false);
      // Handle error - show toast notification, etc.
    },
  });

  // Second mutation: Upload for giveaway
  const imageUploadGiveAwayMutation = useMutation({
    mutationFn: ImageUploadGiveAway,
    onSuccess: () => {
      toast.success("Giveaway upload successful");
      setIsUploading(false);
      // Refetch the data to update the UI
      refetch();
      // Show success message
    },
    onError: (error) => {
      toast.error("Giveaway upload failed");
      console.error("Giveaway image upload failed", error);
      setIsUploading(false);
      // Handle error
    },
  });

  /* ================================================= Handle file upload ================================================= */

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (e.g., 5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size should be less than 5MB");
      return;
    }

    setIsUploading(true);

    // Create FormData
    const formData = new FormData();
    formData.append("image", file); // Adjust the field name based on your API expectations
    formData.append("name", file.name);
    // Add any other fields your API expects

    // Start the upload chain
    uploadToGalleryMutation.mutate({ formData });

    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // rollback
  useEffect(() => {
    if (error?.status === 404 && error?.data?.detail === "Invalid page.") {
      searchParams.set("tpage", "1");
      setSearchParams(searchParams);
    }
  }, [error]);

  const { mutate: toggleActiveStatus } = useMutation({
    mutationFn: ToggleShowGiveawayTemplate,
    onSuccess: (response) => {
      refetch();
      // Extract the image URL/ID from the response - adjust based on your API response structure
      showToast({
        title: "Toggle",
        description: "Successfully updated the template",
        type: "success",
      });
      console.log("The template is_show updated", response);
    },
    onError: (error) => {
      console.error("Ishow update failed", error);
      setIsUploading(false);
      // Handle error - show toast notification, etc.
    },
  });

  const columns =
    // type === "certificates"
    //   ? ColumnList({ refetch: refetch }):
    type === "giveaway-templates"
      ? TemplateColumnList({ refetch, toggleActiveStatus })
      : [];
  const { selectedRowDetails } = useSelectionStore();

  return (
    <>
      <TableCard
        className={cn(
          !activateGiveaway
            ? "pointer-events-none opacity-50 cursor-not-allowed"
            : ""
        )}
        bulkActions
        orientationPosition={type == "giveaway-templates" ? "up" : false}
        inject_ui_table_actions={
          <>
            <div className="flex self-center gap-2 items-center pointer-events-auto cursor-pointer !opacity-100">
              <h4 className="!opacity-100">Active giveaway</h4>
              <Switch
                checked={activateGiveaway}
                disabled={ActivatePending || activePending}
                onCheckedChange={() =>
                  ActivateGiveAway({
                    active: !activateGiveaway,
                    Image: ActiveStatus?.data?.giveaway_image,
                  })
                }
              />
            </div>
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept="image/*"
              style={{ display: "none" }}
            />

            <Button
              variant={"outline"}
              onClick={handleUploadClick}
              disabled={isUploading || !activateGiveaway}
            >
              <Upload />
              {isUploading ? "Uploading..." : "Upload Image"}
            </Button>
          </>
        }
        table_content={{
          mode: "default",
          type: type || "giveaway-templates",
        }}
        formId={type || "giveaway-templates"}
        tableHeaderData={
          pathname == "certificate-manage-template"
            ? []
            : [
                {
                  id: 1,
                  name: "All",
                  count: formatNumber(data?.data?.count as number) || 0,
                },
              ]
        }
        grid={
          <GeneralCard
            isChecked={false}
            type={type === "giveaway-templates" ? "template" : ""}
            content={{
              images: [TestImage, TestImage, TestImage],
              title: "Aayush Maharjan",
              subtitle: "Frontend Developer",
            }}
            onDelete={() => {}}
            onEdit={() => {}}
          />
        }
        form={<TemplateForm />}
        endpoint={endpointData}
        isPending={isPending}
        columns={columns}
        refetch={refetch}
        rows={data?.data?.results ?? []}
        count={data?.data?.count ?? 10}
      />
    </>
  );
};

export default index;
